import React, { useState, useEffect, useRef } from 'react';
import './AccommodationForm.css';
import { useLang } from '../i18n';

// Same Apps Script Web App as the accommodation booking; registrations are routed
// server-side by the `kind: 'registration'` field in the payload.
const SHEET_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbzKt3eo3rTVSS4FmlimKTu0aIYjjRCczWws0gXYlfMbhz0pQTCulM5eUuEn_2upAfSodw/exec';
const SHEET_TOKEN = 'sjy-e85adcb0e9808eabe2bc063d';

const EVENT_YEAR = '2026';

// Per-tournament theme — mirrors AccommodationForm so both modals feel identical.
const THEMES = {
  U13: { accent: '#31969B', bg: '#0b2627' },
  U15: { accent: '#2a7e80', bg: '#0a1f20' },
  U17: { accent: '#267073', bg: '#0b2324' },
};
const DEFAULT_THEME = { accent: '#267073', bg: '#0D2526' };

// Entry fees (EUR): singles flat, doubles per named player — matches the Info pages.
const FEES = {
  U13: { singles: 25, doublesPerPlayer: 20 },
  U15: { singles: 25, doublesPerPlayer: 20 },
  U17: { singles: 40, doublesPerPlayer: 30 },
};

const SINGLES_DISCS = ['BS', 'GS'];
const DOUBLES_DISCS = ['BD', 'GD', 'XD'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Guided tournament registration modal. Steps:
// 1 Club & contact · 2 Singles entries · 3 Doubles entries · 4 Review → 5 success.
// Doubles: player 1 required, player 2 optional (valid entry with one player).
function RegistrationForm({ tournament, onClose }) {
  const { t, tr, lang } = useLang();
  const [step, setStep] = useState(1);
  const [club, setClub] = useState('');
  const [clubCountry, setClubCountry] = useState('');
  const [contact, setContact] = useState({ fname: '', lname: '', phone: '', email: '' });
  const [singles, setSingles] = useState([]);
  const [doubles, setDoubles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  // Scroll lock + keyboard: ESC closes, Enter confirms the step, Tab cycles focus.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && modalRef.current) {
        const el = document.activeElement;
        const tag = el ? el.tagName : '';
        if (tag !== 'BUTTON' && tag !== 'SELECT') {
          const nextBtn = modalRef.current.querySelector('.af-footer .af-btn-primary');
          if (nextBtn && !nextBtn.disabled) { e.preventDefault(); nextBtn.click(); }
        }
      } else if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'button, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const emailValid = EMAIL_RE.test(contact.email);
  const contactValid =
    club.trim() && clubCountry.trim() &&
    contact.fname.trim() && contact.lname.trim() &&
    contact.phone.trim() && emailValid;

  const singlesValid = singles.every((s) => s.first.trim() && s.last.trim());
  // A doubles pair is valid with just player 1 — player 2 stays optional.
  const doublesValid = doubles.every((d) => d.p1f.trim() && d.p1l.trim());
  const entriesCount = singles.length + doubles.length;

  function addSingle() {
    setSingles((prev) => [...prev, { disc: 'BS', first: '', last: '' }]);
  }
  function addPair() {
    setDoubles((prev) => [...prev, { disc: 'BD', p1f: '', p1l: '', p2f: '', p2l: '' }]);
  }
  function setSingle(i, field, value) {
    setSingles((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  }
  function setPair(i, field, value) {
    setDoubles((prev) => prev.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)));
  }
  function removeSingle(i) { setSingles((prev) => prev.filter((_, idx) => idx !== i)); }
  function removePair(i) { setDoubles((prev) => prev.filter((_, idx) => idx !== i)); }

  async function submitForm() {
    setSubmitting(true);
    const payload = {
      token: SHEET_TOKEN,
      kind: 'registration',
      lang,
      tournament,
      club: club.trim(),
      clubCountry: clubCountry.trim(),
      contact: {
        firstName: contact.fname, lastName: contact.lname,
        phone: contact.phone, email: contact.email,
      },
      singles: singles.map((s) => ({
        discipline: s.disc, player: `${s.first} ${s.last}`.trim(), fee: singleFee(),
      })),
      doubles: doubles.map((d) => ({
        discipline: d.disc,
        player1: `${d.p1f} ${d.p1l}`.trim(),
        player2: `${d.p2f} ${d.p2l}`.trim(),
        fee: pairFee(d),
      })),
      total: feesTotal,
      submittedAt: new Date().toISOString(),
    };
    try {
      if (SHEET_WEBHOOK_URL) {
        await fetch(SHEET_WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
        });
      }
      setStep(5);
    } catch (err) {
      window.alert(tr.booking.submitError);
    } finally {
      setSubmitting(false);
    }
  }

  const fees = FEES[tournament] || FEES.U13;
  const singleFee = () => fees.singles;
  const pairFee = (d) => fees.doublesPerPlayer * (((d.p1f.trim() || d.p1l.trim()) ? 1 : 0) + ((d.p2f.trim() || d.p2l.trim()) ? 1 : 0));
  const feesTotal =
    singles.length * fees.singles +
    doubles.reduce((sum, d) => sum + pairFee(d), 0);

  const stepNums = [1, 2, 3, 4];
  const theme = THEMES[tournament] || DEFAULT_THEME;
  const discName = (code) => tr.reg.disciplines[code] || code;

  const renderPlayerInputs = (d, i) => (
    <React.Fragment>
      <div className="af-person-row">
        <input className="af-input" type="text" placeholder={`${tr.reg.player1} — ${t('booking.firstName')}`}
          value={d.p1f} onChange={(e) => setPair(i, 'p1f', e.target.value)} />
        <input className="af-input" type="text" placeholder={`${tr.reg.player1} — ${t('booking.lastName')}`}
          value={d.p1l} onChange={(e) => setPair(i, 'p1l', e.target.value)} />
        <input className="af-input" type="text" placeholder={`${tr.reg.player2opt} — ${t('booking.firstName')}`}
          value={d.p2f} onChange={(e) => setPair(i, 'p2f', e.target.value)} />
        <input className="af-input" type="text" placeholder={`${tr.reg.player2opt} — ${t('booking.lastName')}`}
          value={d.p2l} onChange={(e) => setPair(i, 'p2l', e.target.value)} />
      </div>
    </React.Fragment>
  );

  return (
    <div className="af-overlay" onClick={onClose}>
      <div
        className="af-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('reg.headerTitle')}
        style={{ '--af-accent': theme.accent, '--af-bg': theme.bg }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="af-close af-close--float" onClick={onClose} aria-label="Close">×</button>

        <div className="af-body">
          <div className="af-hd">
            <div className="af-hd-pill">{tournament} · Trenčín {EVENT_YEAR}</div>
            <div className="af-hd-title">{t('reg.headerTitle')}</div>
            <div className="af-hd-sub">Slovak Junior &amp; Youth</div>
          </div>

          {step <= 4 && (
            <div className="af-steps af-steps--seg">
              {stepNums.map((n) => (
                <div key={n} className={`af-seg ${step === n ? 'on' : ''} ${step > n ? 'done' : ''}`} />
              ))}
            </div>
          )}

          {/* STEP 1 — Club & contact */}
          {step === 1 && (
            <React.Fragment>
              <div className="af-section-label">{t('reg.step1Title')}</div>
              <div className="af-grid2">
                <div className="af-field">
                  <span className="af-field-label">{t('reg.club')} *</span>
                  <input className="af-input" type="text" placeholder="BK Trenčín"
                    value={club} onChange={(e) => setClub(e.target.value)} />
                </div>
                <div className="af-field">
                  <span className="af-field-label">{t('reg.country')} *</span>
                  <input className="af-input" type="text" placeholder="Slovakia"
                    value={clubCountry} onChange={(e) => setClubCountry(e.target.value)} />
                </div>
              </div>
              <div className="af-section-label" style={{ marginTop: 18 }}>{t('reg.managerTitle')}</div>
              <div className="af-grid2">
                <div className="af-field">
                  <span className="af-field-label">{t('booking.firstName')} *</span>
                  <input className="af-input" type="text" placeholder="John"
                    value={contact.fname} onChange={(e) => setContact({ ...contact, fname: e.target.value })} />
                </div>
                <div className="af-field">
                  <span className="af-field-label">{t('booking.lastName')} *</span>
                  <input className="af-input" type="text" placeholder="Smith"
                    value={contact.lname} onChange={(e) => setContact({ ...contact, lname: e.target.value })} />
                </div>
              </div>
              <div className="af-field">
                <span className="af-field-label">{t('booking.phone')} *</span>
                <input className="af-input" type="tel" placeholder="+421 900 123 456"
                  value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              </div>
              <div className="af-field">
                <span className="af-field-label">{t('booking.email')} *</span>
                <input
                  className={`af-input ${contact.email ? (emailValid ? 'valid' : 'invalid') : ''}`}
                  type="email" placeholder="john.smith@club.com"
                  value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                {contact.email && !emailValid && (
                  <span className="af-error">{t('booking.emailInvalid')}</span>
                )}
              </div>
            </React.Fragment>
          )}

          {/* STEP 2 — Singles entries */}
          {step === 2 && (
            <React.Fragment>
              <div className="af-section-label">{t('reg.singlesTitle')}</div>
              {singles.length === 0 && <div className="af-note">{t('reg.noEntries')}</div>}
              {singles.map((s, i) => (
                <div className="af-res-item" key={i}>
                  <div className="af-res-head">
                    <select className="af-input" value={s.disc} aria-label={t('reg.discipline')}
                      onChange={(e) => setSingle(i, 'disc', e.target.value)}>
                      {SINGLES_DISCS.map((c) => <option key={c} value={c}>{discName(c)}</option>)}
                    </select>
                    <button className="af-remove-btn" onClick={() => removeSingle(i)}>{t('booking.remove')}</button>
                  </div>
                  <div className="af-person-row">
                    <input className="af-input" type="text" placeholder={t('booking.firstName')}
                      value={s.first} onChange={(e) => setSingle(i, 'first', e.target.value)} />
                    <input className="af-input" type="text" placeholder={t('booking.lastName')}
                      value={s.last} onChange={(e) => setSingle(i, 'last', e.target.value)} />
                  </div>
                </div>
              ))}
              <button className="af-add-room" onClick={addSingle}>+ {t('reg.addSingles')}</button>
            </React.Fragment>
          )}

          {/* STEP 3 — Doubles entries (player 2 optional) */}
          {step === 3 && (
            <React.Fragment>
              <div className="af-section-label">{t('reg.doublesTitle')}</div>
              <div className="af-note">{t('reg.partnerNote')}</div>
              {doubles.map((d, i) => (
                <div className="af-res-item" key={i}>
                  <div className="af-res-head">
                    <select className="af-input" value={d.disc} aria-label={t('reg.discipline')}
                      onChange={(e) => setPair(i, 'disc', e.target.value)}>
                      {DOUBLES_DISCS.map((c) => <option key={c} value={c}>{discName(c)}</option>)}
                    </select>
                    <button className="af-remove-btn" onClick={() => removePair(i)}>{t('booking.remove')}</button>
                  </div>
                  {renderPlayerInputs(d, i)}
                </div>
              ))}
              <button className="af-add-room" onClick={addPair}>+ {t('reg.addDoubles')}</button>
              {entriesCount === 0 && (
                <div className="af-rooms-left low">{t('reg.needOneEntry')}</div>
              )}
            </React.Fragment>
          )}

          {/* STEP 4 — Review */}
          {step === 4 && (
            <React.Fragment>
              <div className="af-section-label">{t('reg.summaryTitle')}</div>
              <div className="af-summary-room">
                <div><strong>{t('booking.tournamentLabel')}</strong> Slovak Youth {tournament} · Trenčín {EVENT_YEAR}</div>
                <div><strong>{t('reg.clubLabel')}</strong> {club} ({clubCountry})</div>
                <div><strong>{t('booking.contactLabel')}</strong> {contact.fname} {contact.lname}</div>
                <div><strong>{t('booking.phoneLabel')}</strong> {contact.phone}</div>
                <div><strong>{t('booking.emailLabel')}</strong> {contact.email}</div>
                <div><strong>{t('reg.entriesLabel')}</strong> {tr.reg.entriesCount(entriesCount)}</div>
              </div>
              {singles.map((s, i) => (
                <div className="af-summary-room" key={`s${i}`}>
                  <div className="af-res-head">
                    <strong>{discName(s.disc)}</strong>
                    <span>{singleFee()} €</span>
                  </div>
                  <div>{s.first} {s.last}</div>
                </div>
              ))}
              {doubles.map((d, i) => (
                <div className="af-summary-room" key={`d${i}`}>
                  <div className="af-res-head">
                    <strong>{discName(d.disc)}</strong>
                    <span>{pairFee(d)} €</span>
                  </div>
                  <div>{tr.reg.player1}: {d.p1f} {d.p1l}</div>
                  {(d.p2f.trim() || d.p2l.trim()) && <div>{tr.reg.player2opt.split(' (')[0]}: {d.p2f} {d.p2l}</div>}
                </div>
              ))}
              <div className="af-total-bar">
                <span>{t('booking.estimatedTotal')}</span>
                <span className="af-total-amount">{feesTotal} €</span>
              </div>
              <div className="af-note">{t('tournament.feesPayment')}</div>
            </React.Fragment>
          )}

          {/* STEP 5 — Success */}
          {step === 5 && (
            <div className="af-success">
              <div className="af-success-icon"><i className="fas fa-check" aria-hidden="true"></i></div>
              <div className="af-success-title">{t('reg.successTitle')}</div>
              <div className="af-success-text">{t('reg.successText')}</div>
            </div>
          )}
        </div>

        {step <= 4 && (
          <div className="af-footer">
            {step > 1
              ? <button className="af-btn-outline" onClick={() => setStep(step - 1)}><i className="fas fa-arrow-left" aria-hidden="true"></i> {t('booking.btnBack')}</button>
              : <span />}
            {step === 1 && <button className="af-btn-primary" disabled={!contactValid} onClick={() => setStep(2)}>{t('reg.btnSingles')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 2 && <button className="af-btn-primary" disabled={!singlesValid} onClick={() => setStep(3)}>{t('reg.btnDoubles')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 3 && <button className="af-btn-primary" disabled={!doublesValid || entriesCount === 0} onClick={() => setStep(4)}>{t('booking.btnReview')} <i className="fas fa-arrow-right" aria-hidden="true"></i></button>}
            {step === 4 && (
              <button className="af-btn-primary" disabled={submitting} onClick={submitForm}>
                {submitting ? t('booking.btnSubmitting') : t('reg.btnSubmit')} <i className="fas fa-paper-plane" aria-hidden="true"></i>
              </button>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="af-footer">
            <span />
            <button className="af-btn-primary" onClick={onClose}>{t('booking.btnClose')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegistrationForm;
